"use client";

import { useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import {
  readAdvertisingConsent,
  readAnalyticsConsent,
} from "@/components/analytics/browser-attribution";
import {
  stripAdvertisingAttributionFromCurrentUrl,
  subscribeToBrowserConsentCategory,
} from "@/components/analytics/browser-consent-lifecycle";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";
import { isSignedUrlSurfacePath } from "@/lib/analytics/signed-url-surfaces";

export const MICROSOFT_CLARITY_PROJECT_ID = "wsz57cz325";

const LIVE_CLARITY_HOSTS = new Set([
  "thesitebook.co.uk",
  "www.thesitebook.co.uk",
  "do.thesitebook.co.uk",
]);

declare global {
  interface Window {
    clarity?: ClarityFunction;
  }
}

type ClarityFunction = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

function hostnameFromBaseUrl(baseUrl: string | undefined): string | null {
  if (!baseUrl) return null;

  try {
    const url = baseUrl.includes("://") ? baseUrl : `https://${baseUrl}`;
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function normalizedHostname(hostname: string | undefined): string | null {
  return hostname ? hostname.toLowerCase() : null;
}

function isLiveClarityHost(hostname: string | null): boolean {
  return hostname !== null && LIVE_CLARITY_HOSTS.has(hostname);
}

export function shouldLoadMicrosoftClarity(
  baseUrl = process.env.NEXT_PUBLIC_APP_BASE_URL,
  browserHostname?: string,
): boolean {
  return (
    isLiveClarityHost(hostnameFromBaseUrl(baseUrl)) ||
    isLiveClarityHost(normalizedHostname(browserHostname))
  );
}

function getServerClarityEnabled() {
  return shouldLoadMicrosoftClarity();
}

function getClientClarityEnabled() {
  if (typeof window === "undefined") return getServerClarityEnabled();
  return shouldLoadMicrosoftClarity(
    process.env.NEXT_PUBLIC_APP_BASE_URL,
    window.location.hostname,
  );
}

// useSyncExternalStore subscribe for both env (one-shot, no real updates) and
// consent (real updates via cookie_consent_change + storage events). Server
// snapshot always returns `null` for consent so the first render matches what
// the server painted (which doesn't have access to the cookie); the client
// snapshot then flips it once useSyncExternalStore re-renders post-hydration.
function subscribeNoop() {
  return () => {};
}

function subscribeToConsent(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  return subscribeToBrowserConsentCategory("analytics", cb);
}

function getServerConsentSnapshot(): "accepted" | "declined" | null {
  return null;
}

function getClientConsentSnapshot(): "accepted" | "declined" | null {
  return readAnalyticsConsent();
}

export function MicrosoftClarity() {
  const envEnabled = useSyncExternalStore(
    subscribeNoop,
    getClientClarityEnabled,
    getServerClarityEnabled,
  );
  // Never load Clarity on bearer-token paths (or /login|/signup carrying a
  // token in ?redirect_url) — it session-records the full URL, token included.
  // See lib/analytics/suppressed-paths.ts.
  const pathname = usePathname();
  const suppressed = useAnalyticsSuppressed(pathname);
  // #2326 round 15: product surfaces render presigned bearer URLs, and Clarity
  // has no attribute-masking hook — so it does not run there at all (see
  // lib/analytics/clarity-scope.ts; PostHog replays them with the mask).
  const enabled =
    envEnabled && !suppressed && !isSignedUrlSurfacePath(pathname);

  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );

  // #2326 round 16: the STOP must run PRE-PAINT. A passive effect fires
  // after React has mounted and painted the destination DOM, so a
  // client-side navigation into a suppressed route would let the still-
  // running recorder observe the newly mounted presigned href/src attributes
  // before `clarity("stop")` lands — exactly the leak this suppression
  // exists to prevent. Layout effects run before the browser paints (the
  // same boundary the PostHog provider uses for its token-route opt-out).
  // #2326 round 16: the STOP must run PRE-PAINT. A passive effect fires
  // after React has mounted and painted the destination DOM, so a
  // client-side navigation into a suppressed route would let the still-
  // running recorder observe the newly mounted presigned href/src attributes
  // before `clarity("stop")` lands — exactly the leak this suppression
  // exists to prevent. Layout effects run before the browser paints (the
  // same boundary the PostHog provider uses for its token-route opt-out).
  useLayoutEffect(() => {
    if (!enabled) {
      window.clarity?.("stop");
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      // Suppressed route (or Clarity disabled) — the pre-paint stop above has
      // already halted the recorder; this passive branch keeps the denial
      // standing for a late-mounting library and documents the intent.
      // (Audit P1, symmetric to the GA ga-disable kill switch.)
      window.clarity?.("stop");
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
      return;
    }

    if (consent !== "accepted") {
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
      return;
    }

    if (readAdvertisingConsent() !== "accepted") {
      stripAdvertisingAttributionFromCurrentUrl();
    }

    let active = true;
    let script = document.getElementById(
      "microsoft-clarity-lib",
    ) as HTMLScriptElement | null;

    if (!window.clarity) {
      const clarity = ((...args: unknown[]) => {
        (clarity.q ??= []).push(args);
      }) as ClarityFunction;
      window.clarity = clarity;
    }

    function applyCurrentConsent(): boolean {
      const accepted = active && readAnalyticsConsent() === "accepted";
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: accepted ? "granted" : "denied",
      });
      return accepted;
    }

    function onLoad() {
      if (script) script.dataset.loaded = "true";
      applyCurrentConsent();
    }

    // Start every pending library in denied mode. A detached script can still
    // finish executing after removal, so its real queue must contain no stale
    // grant that could be drained before our load handler runs.
    window.clarity("consentv2", {
      ad_Storage: "denied",
      analytics_Storage: "denied",
    });

    // PECR/ICO: do not even fetch the Clarity library unless the current,
    // fail-closed cookie read still says accepted. Grant only from onLoad,
    // after the delayed library has executed and we have re-read the cookie.
    if (readAnalyticsConsent() !== "accepted") return;

    if (script?.dataset.loaded === "true") {
      // Resume in case a prior suppressed-route visit stopped the recorder.
      window.clarity?.("start");
      applyCurrentConsent();
    } else {
      if (!script) {
        script = document.createElement("script");
        script.id = "microsoft-clarity-lib";
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${MICROSOFT_CLARITY_PROJECT_ID}`;
        script.addEventListener("load", onLoad, { once: true });
        document.head.appendChild(script);
      } else {
        script.addEventListener("load", onLoad, { once: true });
      }
    }

    return () => {
      active = false;
      script?.removeEventListener("load", onLoad);
      if (script?.dataset.loaded !== "true") script?.remove();
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
    };
  }, [consent, enabled]);

  return null;
}
